import { indirizziFetch } from './indirizziFetch';
import { Reports, ReportCreate, ComponentRead, ElementRead, ElementUpdate, SourceRead, SourceDetail, ReportTemplateRead, IndicePenetrazioneCash, DatiMercato, ElementUpdateIndicePenConValue, Mercato, InformationTransparency, MarketMaker, MarketMakerUpdatePayload, InformationTransparencyUpdatePayload, Venue, Reclamo, ReclamoUpdatePayload, TrasparenzaESG, TrasparenzaESGUpdatePayload, IndiceSolidita, IndiceSoliditapdatePayload, DriverScores, ScenarioScores, BankRankings, AllScenarioWeights, EventSearchPayload, AdesioneMercati, Bank, DocumentClassList, DocumentClassSource, CreateSourceParams, LinkSourceToDocumentClassParams, DocumentClass, ValidationResult, IngestionResponse, IngestRequest, BestExecutionReport, ElementGeneration, RegisterBeSourceParams, SourceCheckRequest, SourceCheckResponse, SourceResolveRequest, SourceResolveResponse, SourceResolveDecision, SourceCheck, SourceIngestion, DocumentSource, isGeneralDocumentType } from '../api/apiTypes';
import { ConductRiskReportCreate, ConductRiskReport, ConductRiskSourcesResponse, ConductRiskValidationResponse, InvestigationSummary } from './apiTypesConductRisk';
import { axiosInstance, deleteWithErrorData, getWithErrorData, getWithErrorDataBlob, patchWithErrorData, postMultipartWithErrorData, postWithErrorData, postWithoutBody, putWithErrorData, toApiError } from './wrappers';

export const fetchReports = async (): Promise<Reports[]> => {
  const config = {
    params: {
      skip: 0,
      limit: 100,
    },
  };
  const { data, error } = await getWithErrorData<Reports[]>(
    indirizziFetch.GET_REPORTS,
    config
  );

  if (error) throw new Error(error);
  return data ?? [];
};

/** GET /api/v1/reports/best_execution — lista BE filtrata server-side. */
export const fetchBestExecutionReports = async (): Promise<BestExecutionReport[]> => {
  const config = { params: { skip: 0, limit: 100 } };
  const { data, error } = await getWithErrorData<BestExecutionReport[]>(
    indirizziFetch.GET_BE_REPORTS,
    config,
  );
  if (error) throw new Error(error);
  return data ?? [];
};

/** GET /api/v1/reports/conduct_risk — lista CR filtrata server-side (include fiscal_year, started_at). */
export const fetchConductRiskReports = async (): Promise<ConductRiskReport[]> => {
  const config = { params: { skip: 0, limit: 100 } };
  const { data, error } = await getWithErrorData<ConductRiskReport[]>(
    indirizziFetch.GET_CR_REPORTS,
    config,
  );
  if (error) throw new Error(error);
  return data ?? [];
};

/**
 * POST /reports/best_execution.
 * Lancia un ApiError che conserva lo status: 409 = fonti non pronte (il messaggio
 * elenca banca/tipo documento), 422 = reference_year fuori range, 400 = banche.
 */
export const createReport = async (payload: ReportCreate): Promise<BestExecutionReport> => {
  const res = await postWithErrorData<BestExecutionReport>(indirizziFetch.CREATE_REPORT, payload);
  if (res.error) throw toApiError(res, 'Errore nella creazione del report');
  return res.data!;
};

export const createConductRiskReport = async (
  payload: ConductRiskReportCreate,
): Promise<ConductRiskReport> => {
  const { data, error } = await postWithErrorData<ConductRiskReport>(
    indirizziFetch.CREATE_CR_REPORT,
    payload,
  );
  if (error) throw new Error(error);
  return data!;
};

export const uploadConductRiskSource = async (
  reportId: string,
  file: File,
  documentClassId: string,
  sourceMimetype?: string,
): Promise<any> => {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('document_class_id', documentClassId);
  if (sourceMimetype) fd.append('source_mimetype', sourceMimetype);

  const { data, error } = await postMultipartWithErrorData<any>(
    indirizziFetch.UPLOAD_CR_SOURCE(reportId),
    fd,
  );
  if (error) throw new Error(error);
  return data;
};

/** POST /api/v1/reports/conduct_risk/{report_id}/start — innesca le DAG dei 9 driver. */
export const startConductRiskReport = async (reportId: string): Promise<any> => {
  const { data, error } = await postWithoutBody<any>(
    indirizziFetch.START_CR_REPORT(reportId),
  );
  if (error) throw new Error(error);
  return data;
};

/** POST /api/v1/reports/conduct_risk/{report_id}/validate.
 *  Ritorna lo stato globale e, se NOT OK, la mappa issues_by_document_type. */
export const validateConductRiskReport = async (
  reportId: string,
): Promise<ConductRiskValidationResponse> => {
  const { data, error } = await postWithoutBody<ConductRiskValidationResponse>(
    indirizziFetch.VALIDATE_CR_REPORT(reportId),
  );
  if (error) throw new Error(error);
  return data!;
};

/** GET /api/v1/reports/conduct_risk/{report_id}/sources — fonti collegate + tipi mancanti. */
export const listConductRiskSources = async (reportId: string): Promise<ConductRiskSourcesResponse> => {
  const { data, error } = await getWithErrorData<ConductRiskSourcesResponse>(
    indirizziFetch.LIST_CR_SOURCES(reportId),
  );
  if (error) throw new Error(error);
  return data ?? { links: [], missing_document_types: [] };
};

/** DELETE /api/v1/reports/conduct_risk/{report_id}/sources/{link_id} — scollega una fonte. */
export const unlinkConductRiskSource = async (
  reportId: string,
  linkId: string,
): Promise<void> => {
  const { error } = await deleteWithErrorData<void>(
    indirizziFetch.UNLINK_CR_SOURCE(reportId, linkId),
  );
  if (error) throw new Error(error);
};

/** Estrae il filename da un header Content-Disposition, supportando
 *  sia `filename="..."` che `filename*=UTF-8''...` (RFC 5987). */
const parseContentDispositionFilename = (header: string | undefined): string | undefined => {
  if (!header) return undefined;
  const utf8 = /filename\*\s*=\s*(?:UTF-8'')?([^;]+)/i.exec(header);
  if (utf8?.[1]) {
    try { return decodeURIComponent(utf8[1].trim().replace(/^"|"$/g, '')); }
    catch { /* fallback sotto */ }
  }
  const plain = /filename\s*=\s*"?([^"';]+)"?/i.exec(header);
  return plain?.[1]?.trim();
};

/** GET /api/v1/reports/conduct_risk/{report_id}/sources/{link_id}/download — file raw.
 *  Ritorna il blob + il filename estratto dall'header Content-Disposition
 *  (il BE lo sintetizza in base al mimetype). Richiede che il BE esponga
 *  l'header via CORS: `Access-Control-Expose-Headers: Content-Disposition`. */
export const downloadConductRiskSource = async (
  reportId: string,
  linkId: string,
): Promise<{ blob: Blob; filename?: string }> => {
  try {
    const response = await axiosInstance.get<Blob>(
      indirizziFetch.DOWNLOAD_CR_SOURCE(reportId, linkId),
      { responseType: 'blob' },
    );
    const cd = response.headers['content-disposition']
            ?? response.headers['Content-Disposition' as never];
    const filename = parseContentDispositionFilename(cd);
    return { blob: response.data, filename };
  } catch (err: any) {
    throw new Error(err?.response?.data?.detail ?? err?.message ?? 'Errore download fonte');
  }
};

/** GET /api/v1/sources/{source_id}/download — bytes di una fonte del catalogo.
 *  Stessa logica di `downloadConductRiskSource` ma senza scoping sul report:
 *  e' la via per le fonti Best Execution, la cui `uri` punta all'object store
 *  (gs://...) e quindi non e' apribile direttamente dal browser. */
export const downloadSource = async (
  sourceId: string,
): Promise<{ blob: Blob; filename?: string }> => {
  try {
    const response = await axiosInstance.get<Blob>(
      indirizziFetch.DOWNLOAD_SOURCE(sourceId),
      { responseType: 'blob' },
    );
    const cd = response.headers['content-disposition']
            ?? response.headers['Content-Disposition' as never];
    const filename = parseContentDispositionFilename(cd);
    return { blob: response.data, filename };
  } catch (err: any) {
    throw new Error(err?.response?.data?.detail ?? err?.message ?? 'Errore download fonte');
  }
};

/** GET /api/v1/reports/conduct_risk/{report_id}/drivers/{component_kind}/export/xlsx
 *  Scarica l'output di un driver CR come Excel. Supportati al momento:
 *  Driver02 (Filiali a Premio), Driver04 (Focus Picchi Ricavi), Driver06 (Correlazione ICF). */
export const fetchExportConductRiskDriverXlsx = async (
  reportId: string,
  componentKind: string,
): Promise<Blob> => {
  const { data, error } = await getWithErrorDataBlob<Blob>(
    indirizziFetch.EXPORT_CR_DRIVER_XLSX(reportId, componentKind),
    { responseType: 'blob' },
  );
  if (error) throw new Error(error);
  return data!;
};

/** GET /api/v1/reports/conduct_risk/{report_id}/investigation-summary
 *  Tabella di aggregazione delle evidenze dei driver di Investigation. */
export const fetchConductRiskInvestigationSummary = async (
  reportId: string,
): Promise<InvestigationSummary> => {
  const { data, error } = await getWithErrorData<InvestigationSummary>(
    indirizziFetch.CR_INVESTIGATION_SUMMARY(reportId),
  );
  if (error) throw new Error(error);
  return data!;
};

/** GET /api/v1/reports/conduct_risk/{report_id}/investigation-summary/export/xlsx
 *  Scarica la tabella di Investigation Summary come Excel. */
export const fetchExportConductRiskInvestigationSummaryXlsx = async (
  reportId: string,
): Promise<Blob> => {
  const { data, error } = await getWithErrorDataBlob<Blob>(
    indirizziFetch.EXPORT_CR_INVESTIGATION_SUMMARY_XLSX(reportId),
    { responseType: 'blob' },
  );
  if (error) throw new Error(error);
  return data!;
};



export const validateReportPrerequisites = async (bankIds: string[]): Promise<ValidationResult> => {
  const payload = {
    bank_ids: bankIds,
  };

  const { data, error } = await postWithErrorData<ValidationResult>(indirizziFetch.VALIDATE_REPORT, payload);

  if (error) {
    console.error('Errore nella validazione report:', error);
    throw new Error(error);
  }

  return data!;
};


export const indicizzaReport = async (
  bankNames: string[],
  force = false,
): Promise<IngestionResponse> => {
  const payload: IngestRequest = {
    bank_names: bankNames,
    force,
  };

  const { data, error } = await postWithErrorData<IngestionResponse>(
    indirizziFetch.INDICIZZA_REPORT,
    payload
  );

  if (error) {
    console.error('Errore durante l\'indicizzazione:', error);
    throw new Error(error);
  }

  return data!;
};

export const fetchReportById = async (reportId: string): Promise<Reports> => {
  const { data, error } = await getWithErrorData<Reports>(indirizziFetch.REPORT_BY_ID(reportId));
  if (error) throw new Error(error);
  return data!;
};

export const listComponents = async (reportId: string): Promise<ComponentRead[]> => {
  const { data, error } = await getWithErrorData<ComponentRead[]>(indirizziFetch.COMPONENTS_BY_REPORT_ID(reportId));
  if (error) throw new Error(error);
  return data ?? [];
};

export const listElements = async (
  reportId: string,
  componentId: string
): Promise<ElementRead[]> => {
  const config = {
    params: {
      skip: 0,
      limit: 100,
    },
  };

  const { data, error } = await getWithErrorData<ElementRead[]>(
    indirizziFetch.ELEMENTS_BY_COMPONENT_ID(reportId, componentId),
    config
  );

  if (error) throw new Error(error);
  return data ?? [];
};

export const listElementsIndiciPenetrazione = async (
  reportId: string,
  componentId: string
): Promise<IndicePenetrazioneCash[]> => {
  const config = { params: { skip: 0, limit: 100 } };

  const { data, error } = await getWithErrorData<any[]>(
    indirizziFetch.ELEMENTS_BY_COMPONENT_ID(reportId, componentId),
    config
  );

  if (error) throw new Error(error);

  return (data ?? [])
    .filter(
      e =>
        e.value &&
        Array.isArray(e.value.raw_data) &&
        typeof e.value.traded_values === 'object'
    )
    .map<IndicePenetrazioneCash>(e => ({
      id: e.id,
      name: e.name,
      status: e.status,
      report_status: e.report_status,
      validation: e.validation,
      raw_data: e.value.raw_data,
      traded_values: e.value.traded_values,
      score: e.score
    }));
};
export const listElementsAdesioneMercati = async (
  reportId: string,
  componentId: string
): Promise<Mercato[]> => {
  const config = { params: { skip: 0, limit: 100 } };

  const { data, error } = await getWithErrorData<any[]>(
    indirizziFetch.ELEMENTS_BY_COMPONENT_ID(reportId, componentId),
    config
  );

  if (error) throw new Error(error);

  return (data ?? [])
};

export const listElementsMarketMaker = async (
  reportId: string,
  componentId: string
): Promise<MarketMaker[]> => {
  const config = { params: { skip: 0, limit: 100 } };

  const { data, error } = await getWithErrorData<any[]>(
    indirizziFetch.ELEMENTS_BY_COMPONENT_ID(reportId, componentId),
    config
  );

  if (error) throw new Error(error);

  return (data ?? [])
};

export const listElementsReclamo = async (
  reportId: string,
  componentId: string
): Promise<Reclamo[]> => {
  const config = { params: { skip: 0, limit: 100 } };

  const { data, error } = await getWithErrorData<any[]>(
    indirizziFetch.ELEMENTS_BY_COMPONENT_ID(reportId, componentId),
    config
  );

  if (error) throw new Error(error);

  return (data ?? [])
};

export const listElementsTrasparenzaEsg = async (
  reportId: string,
  componentId: string
): Promise<TrasparenzaESG[]> => {
  const config = { params: { skip: 0, limit: 100 } };

  const { data, error } = await getWithErrorData<any[]>(
    indirizziFetch.ELEMENTS_BY_COMPONENT_ID(reportId, componentId),
    config
  );

  if (error) throw new Error(error);

  return (data ?? [])
};

export const listElementsIndiciSolidita = async (
  reportId: string,
  componentId: string
): Promise<IndiceSolidita[]> => {
  const config = { params: { skip: 0, limit: 100 } };

  const { data, error } = await getWithErrorData<any[]>(
    indirizziFetch.ELEMENTS_BY_COMPONENT_ID(reportId, componentId),
    config
  );

  if (error) throw new Error(error);

  return (data ?? [])
};

export const listElementsTrasparenzaInformativa = async (
  reportId: string,
  componentId: string
): Promise<InformationTransparency[]> => {
  const config = { params: { skip: 0, limit: 100 } };

  const { data, error } = await getWithErrorData<any[]>(
    indirizziFetch.ELEMENTS_BY_COMPONENT_ID(reportId, componentId),
    config
  );

  if (error) throw new Error(error);

  return (data ?? [])
};

export const readElementsAdesioneMercati = async (
  reportId: string,
  componentId: string,
  elementId: string
): Promise<Mercato[]> => {
  const config = { params: { skip: 0, limit: 100 } };

  const { data, error } = await getWithErrorData<any[]>(
    indirizziFetch.ELEMENT_BY_ID(reportId, componentId, elementId),
    config
  );

  if (error) throw new Error(error);

  return (data ?? []) as Mercato[];
};

export const readElementsMarketMaker = async (
  reportId: string,
  componentId: string,
  elementId: string
): Promise<MarketMaker[]> => {
  const config = { params: { skip: 0, limit: 100 } };

  const { data, error } = await getWithErrorData<any[]>(
    indirizziFetch.ELEMENT_BY_ID(reportId, componentId, elementId),
    config
  );

  if (error) throw new Error(error);

  return (data ?? []) as MarketMaker[];
};

export const readElementsIndiceDiSOlidita = async (
  reportId: string,
  componentId: string,
  elementId: string
): Promise<IndiceSolidita[]> => {
  const config = { params: { skip: 0, limit: 100 } };

  const { data, error } = await getWithErrorData<any[]>(
    indirizziFetch.ELEMENT_BY_ID(reportId, componentId, elementId),
    config
  );

  if (error) throw new Error(error);

  return (data ?? []) as IndiceSolidita[];
};

export const readElementsReclami = async (
  reportId: string,
  componentId: string,
  elementId: string
): Promise<Reclamo[]> => {
  const config = { params: { skip: 0, limit: 100 } };

  const { data, error } = await getWithErrorData<any[]>(
    indirizziFetch.ELEMENT_BY_ID(reportId, componentId, elementId),
    config
  );

  if (error) throw new Error(error);

  return (data ?? []) as Reclamo[];
};


export const readElementsTrasparenza = async (
  reportId: string,
  componentId: string,
  elementId: string
): Promise<InformationTransparency[]> => {
  const config = { params: { skip: 0, limit: 100 } };

  const { data, error } = await getWithErrorData<any[]>(
    indirizziFetch.ELEMENT_BY_ID(reportId, componentId, elementId),
    config
  );

  if (error) throw new Error(error);

  return (data ?? []) as InformationTransparency[];
};

export const readElementsTrasparenzaESG = async (
  reportId: string,
  componentId: string,
  elementId: string
): Promise<TrasparenzaESG[]> => {
  const config = { params: { skip: 0, limit: 100 } };

  const { data, error } = await getWithErrorData<any[]>(
    indirizziFetch.ELEMENT_BY_ID(reportId, componentId, elementId),
    config
  );

  if (error) throw new Error(error);

  return (data ?? []) as TrasparenzaESG[];
};

export const readElementIndiciPenetrazione = async (
  reportId: string,
  componentId: string,
  elementId: string
): Promise<IndicePenetrazioneCash[]> => {
  const config = {
    params: {
      skip: 0,
      limit: 100,
    },
  };

  const { data, error } = await getWithErrorData<ElementRead>(
    indirizziFetch.ELEMENT_BY_ID(reportId, componentId, elementId),
    config
  );

  if (error) throw new Error(error);
  if (!data) return []; 

  if (
    typeof data.value === 'object' &&
    data.value !== null &&
    'raw_data' in data.value &&
    'traded_values' in data.value &&
    Array.isArray((data.value as any).raw_data) &&
    typeof (data.value as any).traded_values === 'object'
  ) {
    return [
      {
        id: data.id,
        name: data.name,
        status: data.status,
        report_status: data.report_status,
        validation: data.validation,
        raw_data: (data.value as any).raw_data,
        traded_values: (data.value as any).traded_values,
        score: data.score
      },
    ];
  }

  return [];
};

export const readElement = async (
  reportId: string,
  componentId: string,
  elementId: string
): Promise<ElementRead> => {
  const { data, error } = await getWithErrorData<ElementRead>(indirizziFetch.ELEMENT_BY_ID(reportId, componentId, elementId));
  if (error) throw new Error(error);
  return data!;
};

export const updateElement = async (
  reportId: string,
  componentId: string,
  elementId: string,
  payload: ElementUpdate
): Promise<ElementRead> => {
  const { data, error } = await postWithErrorData<ElementRead>(indirizziFetch.EDIT_ELEMENT(reportId, componentId, elementId), payload);
  if (error) throw new Error(error);
  return data!;
};

export const updateIndiceDiPentrazione = async (
  reportId: string,
  componentId: string,
  elementId: string,
  payload: ElementUpdateIndicePenConValue
): Promise<DatiMercato> => {
  const { data, error } = await postWithErrorData<DatiMercato>(
    indirizziFetch.EDIT_ELEMENT(reportId, componentId, elementId),
    payload
  );
  if (error) throw new Error(error);
  return data!;
};

export const validaIndiceDiPentrazione = async (
  reportId: string,
  componentId: string,
  elementId: string
): Promise<ElementUpdateIndicePenConValue> => {
  const { data, error } = await postWithErrorData<ElementUpdateIndicePenConValue>(
    indirizziFetch.VALIDATE_ELEMENT(reportId, componentId, elementId),
    null 
  );
  if (error) throw new Error(error);
  return data!;
};

export const validaAdesioneMercati = async (
  reportId: string,
  componentId: string,
  elementId: string
): Promise<Mercato> => {
  const { data, error } = await postWithErrorData<Mercato>(
    indirizziFetch.VALIDATE_ELEMENT(reportId, componentId, elementId),
    null 
  );
  if (error) throw new Error(error);
  return data!;
};

export const updateAdesioneMercati = async (
  reportId: string,
  componentId: string,
  elementId: string,
  payload: { value: AdesioneMercati }   
): Promise<Mercato> => {
  const { data, error } = await postWithErrorData<Mercato>(
    indirizziFetch.EDIT_ELEMENT(reportId, componentId, elementId),
    payload
  );
  if (error) throw new Error(error);
  return data!;
};

export const validaElement = async (
  reportId: string,
  componentId: string,
  elementId: string
): Promise<ElementRead> => {
  const { data, error } = await postWithErrorData<ElementRead>(
    indirizziFetch.VALIDATE_ELEMENT(reportId, componentId, elementId),
    null 
  );
  if (error) throw new Error(error);
  return data!;
};

export const readSource = async (
  reportId: string,
  componentId: string,
  elementId: string,
  sourceId: string
): Promise<SourceDetail> => {
  const url = indirizziFetch.SOURCE_BY_ID(reportId, componentId, elementId, sourceId);
  const { data, error } = await getWithErrorData<SourceDetail>(url);
  if (error) throw new Error(error);
  return data!;
};

export const listSources = async (
  reportId: string,
  componentId: string,
  elementId: string,
  skip = 0,
  limit = 100
): Promise<SourceRead[]> => {
  const url = `${indirizziFetch.SOURCES_BY_ELEMENT_ID(
    reportId,
    componentId,
    elementId
  )}?skip=${skip}&limit=${limit}`;

  const { data, error } = await getWithErrorData<SourceRead[]>(url);

  if (error) throw new Error(error);
  return data ?? [];
};


export const listVenues = async (
  skip = 0,
  limit = 100,
  filters?: {
    iso_country_code_in?: string;
    iso_country_code_not_in?: string[];
    mics?: string[];
    operating_mics?: string[];
    search_text?: string; 
  }
): Promise<Venue[]> => {
  const body = {
    mics: filters?.mics ?? [],
    operating_mics: filters?.operating_mics ?? [],
    market_name_starts_with: '',
    market_name_contains: filters?.search_text ?? '',
    legal_entity_name_starts_with: filters?.search_text ?? '',
    iso_country_code_in: filters?.iso_country_code_in ? [filters.iso_country_code_in] : [],
    iso_country_code_not_in: filters?.iso_country_code_not_in ?? [],
  };

  const { data, error } = await postWithErrorData<Venue[]>(
    indirizziFetch.SEARCH_VENUES(skip, limit),
    body
  );

  if (error) throw new Error(error);
  return data ?? [];
};
export const fetchTemplates = async (): Promise<ReportTemplateRead[]> => {
  const config = {
    params: {
      skip: 0,
      limit: 100,
    },
  };

  const { data, error } = await getWithErrorData<ReportTemplateRead[]>(
    indirizziFetch.GET_TEMPLATES,
    config
  );

  if (error) throw new Error(error);
  return data ?? [];
};


export const updateMarkerMaker = async (
  reportId: string,
  componentId: string,
  elementId: string,
  payload: MarketMakerUpdatePayload // Aggiorna questo tipo
): Promise<MarketMaker> => {
  const { data, error } = await postWithErrorData<MarketMaker>(
    indirizziFetch.EDIT_ELEMENT(reportId, componentId, elementId),
    payload
  );
  if (error) throw new Error(error);
  return data!;
};

export const updateReclami = async (
  reportId: string,
  componentId: string,
  elementId: string,
  payload: ReclamoUpdatePayload // Aggiorna questo tipo
): Promise<Reclamo> => {
  const { data, error } = await postWithErrorData<Reclamo>(
    indirizziFetch.EDIT_ELEMENT(reportId, componentId, elementId),
    payload
  );
  if (error) throw new Error(error);
  return data!;
};

export const updateTrasparenzaESG = async (
  reportId: string,
  componentId: string,
  elementId: string,
  payload: TrasparenzaESGUpdatePayload // Aggiorna questo tipo
): Promise<TrasparenzaESG> => {
  const { data, error } = await postWithErrorData<TrasparenzaESG>(
    indirizziFetch.EDIT_ELEMENT(reportId, componentId, elementId),
    payload
  );
  if (error) throw new Error(error);
  return data!;
};

export const updateSolidita = async (
  reportId: string,
  componentId: string,
  elementId: string,
  payload: IndiceSoliditapdatePayload // Aggiorna questo tipo
): Promise<IndiceSolidita> => {
  const { data, error } = await postWithErrorData<IndiceSolidita>(
    indirizziFetch.EDIT_ELEMENT(reportId, componentId, elementId),
    payload
  );
  if (error) throw new Error(error);
  return data!;
};

export const validaMarkerMaker = async (
  reportId: string,
  componentId: string,
  elementId: string
): Promise<MarketMaker> => {
  const { data, error } = await postWithErrorData<MarketMaker>(
    indirizziFetch.VALIDATE_ELEMENT(reportId, componentId, elementId),
    null 
  );
  if (error) throw new Error(error);
  return data!;
};

export const validaReclamo = async (
  reportId: string,
  componentId: string,
  elementId: string
): Promise<Reclamo> => {
  const { data, error } = await postWithErrorData<Reclamo>(
    indirizziFetch.VALIDATE_ELEMENT(reportId, componentId, elementId),
    null 
  );
  if (error) throw new Error(error);
  return data!;
};

export const updateTrasparenza = async (
  reportId: string,
  componentId: string,
  elementId: string,
  payload: InformationTransparencyUpdatePayload 
): Promise<InformationTransparency> => {
  const { data, error } = await postWithErrorData<InformationTransparency>(
    indirizziFetch.EDIT_ELEMENT(reportId, componentId, elementId),
    payload
  );
  if (error) throw new Error(error);
  return data!;
};

export const validaTrasparenza = async (
  reportId: string,
  componentId: string,
  elementId: string
): Promise<InformationTransparency> => {
  const { data, error } = await postWithErrorData<InformationTransparency>(
    indirizziFetch.VALIDATE_ELEMENT(reportId, componentId, elementId),
    null 
  );
  if (error) throw new Error(error);
  return data!;
};

export const validaTrasparenzaESG = async (
  reportId: string,
  componentId: string,
  elementId: string
): Promise<TrasparenzaESG> => {
  const { data, error } = await postWithErrorData<TrasparenzaESG>(
    indirizziFetch.VALIDATE_ELEMENT(reportId, componentId, elementId),
    null 
  );
  if (error) throw new Error(error);
  return data!;
};

export const validaSolidita = async (
  reportId: string,
  componentId: string,
  elementId: string
): Promise<IndiceSolidita> => {
  const { data, error } = await postWithErrorData<IndiceSolidita>(
    indirizziFetch.VALIDATE_ELEMENT(reportId, componentId, elementId),
    null 
  );
  if (error) throw new Error(error);
  return data!;
};

export const fetchFileLatestById = async (reportId: string): Promise<any> => {
  const { data, error } = await getWithErrorData<any>(indirizziFetch.FILE_LATEST(reportId));
  if (error) throw new Error(error);
  return data!;
};

export const createOrUpdateReportFile = async (
  reportId: string,
  jsonContent?: any
): Promise<any> => {
  const body = {
    file_name: `report_${reportId}.json`,
    json_content: jsonContent ?? {}, // se non passo dati, invio oggetto vuoto
  };

  const { data, error } = await postWithErrorData<any>(
    indirizziFetch.REPORT_FILES(reportId),
    body
  );

  if (error) throw new Error(error);
  return data!;
};

export const createReportFile = async (reportId: string): Promise<any> => {
  const { data, error } = await postWithoutBody<any>(
    indirizziFetch.REPORT_FILES(reportId)
  );

  if (error) throw new Error(error);
  return data!;
};



export const fetchDriverScoresById = async (reportId: string): Promise<DriverScores> => {
  const { data, error } = await getWithErrorData<DriverScores>(indirizziFetch.DRIVER_SCORES(reportId));
  if (error) throw new Error(error);
  return data!;
};

export const fetchScenarioScoresById = async (reportId: string): Promise<ScenarioScores> => {
  const { data, error } = await getWithErrorData<ScenarioScores>(indirizziFetch.SCENARIO_SCORES(reportId));
  if (error) throw new Error(error);
  return data!;
};

export const fetchFinalRankingById = async (reportId: string): Promise<BankRankings> => {
  const { data, error } = await getWithErrorData<BankRankings>(indirizziFetch.FINAL_RANKING(reportId));
  if (error) throw new Error(error);
  return data!;
};

export const fetchScenarioWeightsById = async (reportId: string): Promise<AllScenarioWeights> => {
  const { data, error } = await getWithErrorData<AllScenarioWeights>(indirizziFetch.SCENARI_WEIGHTS(reportId));
  if (error) throw new Error(error);
  return data!;
};

export const fetchExportDocxById = async (reportId: string, clientName?: string): Promise<Blob> => {
  const url = clientName
    ? `${indirizziFetch.EXPORT_DOCX(reportId)}?client_name=${encodeURIComponent(clientName)}`
    : indirizziFetch.EXPORT_DOCX(reportId);
  const { data, error } = await getWithErrorDataBlob<Blob>(url, { responseType: 'blob' });
  if (error) throw new Error(error);
  return data!;
};

export const fetchExportXlsxById = async (reportId: string): Promise<Blob> => {
  const { data, error } = await getWithErrorDataBlob<Blob>(
    indirizziFetch.EXPORT_XLSX(reportId),
    { responseType: 'blob' } // 👈 serve per scaricare file binari
  );
  if (error) throw new Error(error);
  return data!;
};

export const fetchExportComponentXlsxById = async (reportId: string, componentId: string): Promise<Blob> => {
  const { data, error } = await getWithErrorDataBlob<Blob>(
    indirizziFetch.EXPORT_COMPONENT_XLSX(reportId, componentId),
    { responseType: 'blob' } // 👈 serve per scaricare file binari
  );
  if (error) throw new Error(error);
  return data!;
};

export const searchEvents  = async (
  payload: EventSearchPayload
): Promise<any[]> => {
  const { data, error } = await postWithErrorData<any[]>(
    indirizziFetch.SEARCH_EVENTS,
    payload
  );

  if (error) throw new Error(error);

  return data ?? [];
};


export const fetchApprovalById = async (reportId: string): Promise<any> => {
  const { data, error } = await getWithErrorData<any>(indirizziFetch.APPROVE(reportId));
  if (error) throw new Error(error);
  return data!;
};

export const fetchReopenById = async (reportId: string): Promise<any> => {
  const { data, error } = await getWithErrorData<any>(indirizziFetch.REOPEN(reportId));
  if (error) throw new Error(error);
  return data!;
};

export const fetchCompleteById = async (reportId: string): Promise<any> => {
  const { data, error } = await getWithErrorData<any>(indirizziFetch.COMPLETE(reportId));
  if (error) throw new Error(error);
  return data!;
};

export const fetchBanks = async (): Promise<Bank[]> => {
  const config = {
    params: {
      skip: 0,
      limit: 100,
    },
  };
  const { data, error } = await getWithErrorData<Bank[]>(
    indirizziFetch.GET_BANKS,
    config
  );
  
  if (error) throw new Error(error);
  return data ?? [];
};


export const createBank = async (name: string): Promise<Bank> => {
  const body = { name }; // JSON valido, nessuna virgola finale
  const { data, error } = await postWithErrorData<Bank>(indirizziFetch.POST_bANKS, body);

  if (error) throw new Error(error);
  if (!data) throw new Error('Nessun dato restituito dalla creazione banca');
  return data;
};

// POST per inizializzare document classes
export const initializeDocumentClasses = async (bankId: string): Promise<void> => {
  const { error } = await postWithErrorData<void>(
    indirizziFetch.INITIALIZE_BANK_DOCUMENT_CLASSES(bankId),
    {} 
  );
  if (error) throw new Error(error);
};

export const fetchBankById = async (bankId: string): Promise<Bank> => {
  const { data, error } = await getWithErrorData<Bank>(indirizziFetch.BANK_BY_ID(bankId));
  if (error) throw new Error(error);
  return data!;
};

export const fetchDocumentBankById = async (bankId: string): Promise<DocumentClassList> => {
  const { data, error } = await getWithErrorData<DocumentClassList>(indirizziFetch.DOCUMENT_BANK_BY_ID(bankId));
  if (error) throw new Error(error);
  return data!;
};

// Aggiornamento lista alias per una banca
export const updateBankAliases = async (bankId: string, aliases: string[]): Promise<Bank> => {
  const { data, error } = await putWithErrorData<Bank>(indirizziFetch.UPDATE_BANK_ALIASES(bankId), { aliases })

  if (error) throw new Error(error)
  if (!data) throw new Error('Nessun dato restituito dall\'aggiornamento alias')
  return data
}

export const fetchDocumentSourcesByClass = async (
  documentClassId: string
): Promise<DocumentClassSource[]> => {
  const { data, error } = await getWithErrorData<DocumentClassSource[]>(
    indirizziFetch.DOCUMENT_CLASS_SOURCES(documentClassId)
  );

  if (error) throw new Error(error);
  return data ?? [];
};


/**
 * @deprecated Route generica: non conosce la banca, quindi i bytes finiscono fuori
 * dal layout per banca e non viene creata nessuna classe al volo. Per il Best
 * Execution usare `registerBestExecutionSource`.
 */
export const createSource = async (params: CreateSourceParams): Promise<DocumentClassSource> => {
  const { data, error } = await postWithErrorData<DocumentClassSource>(indirizziFetch.CREATE_SOURCES, params)
  
  if (error) throw new Error(error)
  if (!data) throw new Error('Nessun dato restituito dalla creazione della fonte')
  return data
}
/** @deprecated Seconda meta' della coppia legacy: vedi `registerBestExecutionSource`. */
export const linkSourceToDocumentClass = async (
  params: LinkSourceToDocumentClassParams
): Promise<DocumentClassSource> => {
  const { data, error } = await postWithErrorData<DocumentClassSource>(
    indirizziFetch.ADD_SOURCE_TO_DOCUMENT_CLASS,
    params
  )

  if (error) throw new Error(error)
  if (!data) throw new Error('Nessun dato restituito dal collegamento fonte-documento')

  return data
}


export const fetchAllBeDocumentClasses = async (): Promise<DocumentClass[]> => {
  const { data, error } = await getWithErrorData<DocumentClass[]>(indirizziFetch.BE_DOCUMENT_CLASSES);
  if (error) throw new Error(error);
  return data ?? [];
};

export const fetchGeneralDocumentClasses = async (): Promise<DocumentClass[]> => {
  const { data, error } = await getWithErrorData<DocumentClass[]>(indirizziFetch.BE_DOCUMENT_CLASSES_GENERAL);
  if (error) throw new Error(error);
  return data ?? [];
};

export const fetchCRDocumentClassByType = async (
  documentType: string,
): Promise<DocumentClass> => {
  const { data, error } = await getWithErrorData<DocumentClass>(
    indirizziFetch.CR_DOCUMENT_CLASS_BY_TYPE(documentType),
  );
  if (error) throw new Error(error);
  return data!;
};

export const deactivateDocumentSource = async (documentSourceId: string): Promise<void> => {
  const { error } = await patchWithErrorData(`${indirizziFetch.DEACTIVATE_DOCUMENT}/${documentSourceId}/deactivate`, {});
  if (error) throw new Error(`Errore disattivando il documento ${documentSourceId}: ${error}`);
};

// ===========================================================================
// Best Execution — ciclo di vita delle fonti (release di hardening)
// ===========================================================================

/**
 * POST /reports/best_execution/sources — registrazione fonte in una chiamata sola.
 *
 * Sostituisce la coppia `createSource` + `linkSourceToDocumentClass`: quelle route
 * generiche non conoscono la banca, quindi i bytes finiscono fuori dal layout per
 * banca e non creano la classe al volo.
 *
 * Attenzione: e' sincrona e include il download, quindi su un file remoto grosso
 * puo' impiegare fino a ~30 secondi.
 */
export const registerBestExecutionSource = async (
  params: RegisterBeSourceParams,
): Promise<DocumentClassSource> => {
  const { document_type, bank_id, uri, file, source_mimetype } = params;

  const hasUri = Boolean(uri && uri.trim());
  const hasFile = Boolean(file);
  if (hasUri === hasFile) {
    throw new Error('Serve esattamente uno tra URL e file.');
  }
  if (isGeneralDocumentType(document_type) && bank_id) {
    throw new Error(`Il tipo documento "${document_type}" e' generale: non deve avere una banca.`);
  }
  if (!isGeneralDocumentType(document_type) && !bank_id) {
    throw new Error(`Il tipo documento "${document_type}" richiede una banca.`);
  }

  const formData = new FormData();
  formData.append('document_type', document_type);
  if (bank_id) formData.append('bank_id', bank_id);
  if (hasUri) formData.append('uri', uri!.trim());
  if (hasFile) formData.append('file', file as File);
  if (source_mimetype) formData.append('source_mimetype', source_mimetype);

  const res = await postMultipartWithErrorData<DocumentClassSource>(
    indirizziFetch.BE_REGISTER_SOURCE,
    formData,
  );
  // Il `detail` del 400 e' scritto per l'utente (URL che serve una pagina di login,
  // mismatch di MIME, download fallito): va mostrato cosi' com'e'.
  if (res.error) throw toApiError(res, 'Errore nella registrazione della fonte');
  return res.data!;
};

/**
 * POST /reports/best_execution/check-sources — risponde 202.
 *
 * Ogni fetch e' un download vero: va offerto come bottone deliberato, mai al
 * caricamento pagina e mai dentro il polling di /validate.
 */
export const checkSources = async (bankNames: string[]): Promise<SourceCheckResponse> => {
  const payload: SourceCheckRequest = { bank_names: bankNames };
  const res = await postWithErrorData<SourceCheckResponse>(indirizziFetch.BE_CHECK_SOURCES, payload);
  if (res.error) throw toApiError(res, 'Errore durante il controllo delle fonti');
  return res.data!;
};

/**
 * POST /reports/best_execution/sources/{id}/resolve — decide su una fonte cambiata.
 *
 * `keep`: resta in uso la versione registrata, l'hash corrente viene preso per buono.
 * `switch`: i bytes attuali diventano una nuova fonte che sostituisce la vecchia, e
 * tutti i link attivi si spostano — su tutte le banche che condividono quella fonte.
 * Dopo uno switch serve reindicizzare: `needs_ingestion` dice quali classi.
 */
export const resolveSource = async (
  sourceId: string,
  decision: SourceResolveDecision,
  uri?: string,
): Promise<SourceResolveResponse> => {
  const payload: SourceResolveRequest = { decision };
  if (decision === 'switch' && uri && uri.trim()) payload.uri = uri.trim();

  const res = await postWithErrorData<SourceResolveResponse>(
    indirizziFetch.BE_RESOLVE_SOURCE(sourceId),
    payload,
  );
  if (res.error) throw toApiError(res, 'Errore nella risoluzione della fonte');
  return res.data!;
};

/** GET /sources/{id}/checks — storico dei controlli, dal piu' recente. */
export const fetchSourceChecks = async (
  sourceId: string,
  limit = 100,
): Promise<SourceCheck[]> => {
  const { data, error } = await getWithErrorData<SourceCheck[]>(
    indirizziFetch.SOURCE_CHECKS(sourceId),
    { params: { skip: 0, limit } },
  );
  if (error) throw new Error(error);
  return data ?? [];
};

/** GET /sources/{id}/ingestions — storico delle indicizzazioni, dal piu' recente. */
export const fetchSourceIngestions = async (
  sourceId: string,
  limit = 100,
): Promise<SourceIngestion[]> => {
  const { data, error } = await getWithErrorData<SourceIngestion[]>(
    indirizziFetch.SOURCE_INGESTIONS(sourceId),
    { params: { skip: 0, limit } },
  );
  if (error) throw new Error(error);
  return data ?? [];
};

/** Outcome che rappresentano una decisione conclusa o in attesa (gli altri sono rumore). */
const DECISIVE_CHECK_OUTCOMES: SourceCheck['outcome'][] = [
  'unchanged',
  'changed',
  'acknowledged',
  'switched',
];

/**
 * Trova le fonti di una classe che hanno una decisione pendente.
 *
 * /validate segnala la *classe*, non la fonte, e la lista dei link non porta
 * ancora l'ultimo outcome. Quindi: elenca i link attivi della classe e per ognuno
 * legge lo storico, fermandosi al primo outcome decisivo. `changed` = da risolvere.
 * Le classi hanno poche fonti, quindi sono una o due chiamate in piu' per classe.
 *
 * TODO: il BE ha in programma di esporre l'ultimo outcome direttamente nella lista
 * dei link — quando arriva, questa funzione diventa una sola chiamata.
 */
export const findChangedSourcesForClass = async (
  documentClassId: string,
): Promise<Array<{ source: DocumentSource; link: DocumentClassSource; lastCheck: SourceCheck }>> => {
  const links = (await fetchDocumentSourcesByClass(documentClassId)).filter((l) => l.is_active);

  const results = await Promise.all(
    links.map(async (link) => {
      try {
        const checks = await fetchSourceChecks(link.source_catalog_id, 20);
        const lastCheck = checks.find((c) => DECISIVE_CHECK_OUTCOMES.includes(c.outcome));
        if (!lastCheck || lastCheck.outcome !== 'changed') return null;
        return { source: link.source, link, lastCheck };
      } catch (err) {
        console.error(`Storico check non leggibile per la fonte ${link.source_catalog_id}:`, err);
        return null;
      }
    }),
  );

  return results.filter(
    (r): r is { source: DocumentSource; link: DocumentClassSource; lastCheck: SourceCheck } =>
      r !== null,
  );
};

/**
 * GET .../elements/{id}/generations — storico di come il valore dell'elemento
 * e' stato prodotto, dal piu' recente. `reused` con `applied: false` significa
 * che il valore in cache c'era ma l'elemento era gia' stato modificato a mano.
 */
export const fetchElementGenerations = async (
  reportId: string,
  componentId: string,
  elementId: string,
  limit = 50,
): Promise<ElementGeneration[]> => {
  const { data, error } = await getWithErrorData<ElementGeneration[]>(
    indirizziFetch.ELEMENT_GENERATIONS(reportId, componentId, elementId),
    { params: { skip: 0, limit } },
  );
  if (error) throw new Error(error);
  return data ?? [];
};
